import styled from '@emotion/styled';
import _ from 'lodash';
import { useState, useEffect } from 'react';
import NumberFormat from 'react-number-format';

// Imported Components
import { H2, H6 } from '../text-elements/oc-h';

// Styled Components
const Container = styled.div`
  display: block;
  margin: 0 auto;
  max-width: 1170px;
  position: relative;
  width: 100%;
`;

const Table = styled.table`
  border-collapse: collapse;
  border-spacing: 0;
  color: #333;
  display: inline-block;
  font-size: 14px;
  margin-bottom: 16px;
  min-height: 0.01%;
  overflow-x: auto;
  width: 100%;

  &.four-up {
    th,
    td {
      width: 25%;
    }
  }

  &.five-up {
    th,
    td {
      width: 20%;
    }
  }

  tbody tr:nth-of-type(even) {
    background: #f8f8f8;
  }

  .pink {
    color: #d40f7d;
  }

  .strong {
    font-weight: bold;
  }
`;

const TableHeader = styled.th`
  border-bottom: 2px solid #bcbcbc;
  border-left: 1px solid #bcbcbc;
  border-top: 2px solid #bcbcbc;
  font-size: 14px;
  padding: 8px 12px;
  text-transform: none;

  &:first-of-type {
    border-left: 0;
    text-align: left;
  }
`;

const TableRow = styled.tr`
  &.divider {
    border-top: 8px solid #bcbcbc;
  }
`;

const TableData = styled.td`
  border-bottom: 1px solid #bcbcbc;
  border-left: 1px solid #bcbcbc;
  min-width: 184px;
  padding: 8px 12px;
  text-align: center;

  &:first-of-type {
    border-left: 0;
  }

  &.left {
    text-align: left;
  }
`;

const ProductName = styled.strong`
  display: inline-block;
  font-size: 16px;
  text-transform: uppercase;
  width: 100%;
`;

const Input = styled(NumberFormat, { displayType: 'input' })`
  appearance: none;
  border-radius: 0;
  border: 1px solid #585858;
  display: inline-block;
  font-size: 16px;
  padding: 14px 8px;
  position: relative;
  width: 100%;
`;

const ResultContainer = styled.div`
  display: inline-block;
  margin-right: 4%;
  min-width: 242px;
  position: relative;

  &:last-of-type {
    margin-right: 0;
  }

  h2 {
    color: #d40f7d;
  }
`;

const BrandingText = styled.p`
  font-size: 14px;
  margin-bottom: 16px;
  margin-top: 0;
`;

// eslint-disable-next-line no-empty-pattern
const PadpPromoCalc = ({}) => {
  const [exteriorDoors, setExteriorDoors] = useState(0);
  const [interiorDoors, setInteriorDoors] = useState(0);
  const [fiberglasPurchases, setFiberglasPurchases] = useState(55000);
  const [loosefillPurchases, setLoosefillPurchases] = useState(12000);
  const [foamularPurchases, setFoamularPurchases] = useState(10000);
  const [thermafiberPurchases, setThermafiberPurchases] = useState(3500);
  const [roofingPurchases, setRoofingPurchases] = useState(30000);
  const [tierLevel, setTierLevel] = useState('diamond');

  const categoryCount = [
    exteriorDoors,
    interiorDoors,
    fiberglasPurchases,
    loosefillPurchases,
    foamularPurchases,
    thermafiberPurchases,
    roofingPurchases,
  ].filter(v => v > 0).length;

  const insulationFundingRate = {
    'not-available': 0,
    silver: 0.01,
    gold: 0.02,
    diamond: 0.03,
  };

  const roofingFundingRate = {
    'not-available': 0,
    silver: 0.01,
    gold: 0.01,
    diamond: 0.01,
  };

  const fiberglasFieldName = 'Pink Next Gen™ Fiberglas™';
  const loosefillFieldName = 'ProCat® Loosefill';
  const foamularFieldName = 'Foamular®';
  const thermafiberFieldName = 'Thermafiber®';
  const roofingFieldName = 'Roofing Shingles and Components';

  const fundingRatePercentage = rate => `${rate * 100}%`;

  const getDoorFundingRates = () => {
    switch (tierLevel) {
      case 'silver':
        return { exterior: 1.0, interior: 0.5 };
      case 'gold':
      case 'diamond':
        return { exterior: 2.0, interior: 1.0 };
      default:
        return { exterior: 0, interior: 0 };
    }
  };

  const getExteriorDoorFunds = () => {
    const { exterior } = getDoorFundingRates();
    return exteriorDoors * exterior;
  };

  const getInteriorDoorFunds = () => {
    const { interior } = getDoorFundingRates();
    return interiorDoors * interior;
  };

  const calculateDoorsFunds = () => {
    const { exterior, interior } = getDoorFundingRates();
    return exteriorDoors * exterior + interiorDoors * interior;
  };

  const calculatePromoFunds = (value, rate = insulationFundingRate[tierLevel]) => value * rate;

  const calculateAdditionalFunds = value => {
    const diamondFunds = calculatePromoFunds(value, insulationFundingRate.diamond);
    const currentFunds = calculatePromoFunds(value);

    return diamondFunds - currentFunds;
  };

  const calculateTotalFunding = () => {
    const fiberglasFunds = calculatePromoFunds(fiberglasPurchases);
    const loosefillFunds = calculatePromoFunds(loosefillPurchases);
    const foamularFunds = calculatePromoFunds(foamularPurchases);
    const thermafiberFunds = calculatePromoFunds(thermafiberPurchases);
    const roofingFunds = calculatePromoFunds(roofingPurchases, roofingFundingRate[tierLevel]);
    const doorsFunds = calculateDoorsFunds();

    return fiberglasFunds + loosefillFunds + foamularFunds + thermafiberFunds + roofingFunds + doorsFunds;
  };

  const updateFunds = (event, categoryCallback) => {
    const value = event.floatValue ? event.floatValue : 0;
    categoryCallback(value);
  };

  const updateExteriorDoors = event => {
    updateFunds(event, setExteriorDoors);
  };

  const updateInteriorDoors = event => {
    updateFunds(event, setInteriorDoors);
  };

  const updateFiberglas = event => {
    updateFunds(event, setFiberglasPurchases);
  };

  const updateLoosefill = event => {
    updateFunds(event, setLoosefillPurchases);
  };

  const updateFoamular = event => {
    updateFunds(event, setFoamularPurchases);
  };

  const updateThermafiber = event => {
    updateFunds(event, setThermafiberPurchases);
  };

  const updateRoofing = event => {
    updateFunds(event, setRoofingPurchases);
  };

  const trackUpdate = fieldName => {
    if (typeof _satellite !== 'undefined') {
      _satellite.track('calc-engagement', {
        calcName: 'PADP Promo Funds Calculator',
        fieldUpdated: fieldName,
      });
    }
  };

  useEffect(() => {
    let spendingCategories = 0;
    [
      exteriorDoors,
      interiorDoors,
      fiberglasPurchases,
      loosefillPurchases,
      foamularPurchases,
      thermafiberPurchases,
      roofingPurchases,
      // eslint-disable-next-line array-callback-return
    ].map(purchases => {
      // eslint-disable-next-line no-plusplus
      if (purchases > 0) spendingCategories++;
    });

    switch (spendingCategories) {
      case 4:
      case 5:
      case 6:
      case 7:
        setTierLevel('diamond');
        break;
      case 2:
      case 3:
        setTierLevel('gold');
        break;
      case 1:
        setTierLevel('silver');
        break;
      default:
        setTierLevel('not-available');
    }
  }, [
    exteriorDoors,
    interiorDoors,
    fiberglasPurchases,
    loosefillPurchases,
    foamularPurchases,
    thermafiberPurchases,
    roofingPurchases,
  ]);

  const fiberglasAdditionalFunds = calculateAdditionalFunds(fiberglasPurchases);
  const loosefillAdditionalFunds = calculateAdditionalFunds(loosefillPurchases);
  const foamularAdditionalFunds = calculateAdditionalFunds(foamularPurchases);
  const thermafiberAdditionalFunds = calculateAdditionalFunds(thermafiberPurchases);

  return (
    <Container>
      <Table className="four-up">
        <thead>
          <TableRow>
            <TableHeader>Category</TableHeader>
            <TableHeader>Purchases</TableHeader>
            <TableHeader>Funding Rate</TableHeader>
            <TableHeader className="pink">Potential Promotional Funds</TableHeader>
          </TableRow>
        </thead>
        <tbody>
          <TableRow>
            <TableData className="left">
              <ProductName>Exterior Doors</ProductName>
            </TableData>
            <TableData>
              <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Input
                  allowEmptyFormatting
                  allowNegative={false}
                  decimalScale={0}
                  onBlur={() => trackUpdate('Exterior Doors')}
                  onValueChange={updateExteriorDoors}
                  thousandSeparator
                  value={exteriorDoors}
                />
                <span style={{ marginLeft: '8px' }}>Units</span>
              </div>
            </TableData>
            <TableData>
              {tierLevel === 'not-available' ? '-' : `$${tierLevel === 'silver' ? '1.00' : '2.00'} / unit`}
            </TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={2}
                displayType="text"
                fixedDecimalScale
                prefix="$"
                thousandSeparator
                value={getExteriorDoorFunds()}
              />
            </TableData>
          </TableRow>

          <TableRow>
            <TableData className="left">
              <ProductName>Interior Doors</ProductName>
            </TableData>
            <TableData>
              <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Input
                  allowEmptyFormatting
                  allowNegative={false}
                  decimalScale={0}
                  onBlur={() => trackUpdate('Interior Doors')}
                  onValueChange={updateInteriorDoors}
                  thousandSeparator
                  value={interiorDoors}
                />
                <span style={{ marginLeft: '8px' }}>Units</span>
              </div>
            </TableData>
            <TableData>
              {tierLevel === 'not-available' ? '-' : `$${tierLevel === 'silver' ? '0.50' : '1.00'} / unit`}
            </TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={2}
                displayType="text"
                fixedDecimalScale
                prefix="$"
                thousandSeparator
                value={getInteriorDoorFunds()}
              />
            </TableData>
          </TableRow>

          <TableRow className="divider">
            <TableData className="left">
              <ProductName>{fiberglasFieldName}</ProductName>
            </TableData>
            <TableData>
              <Input
                allowEmptyFormatting
                allowNegative={false}
                decimalScale={0}
                onBlur={() => trackUpdate(fiberglasFieldName)}
                onValueChange={event => updateFiberglas(event)}
                prefix="$"
                thousandSeparator
                value={fiberglasPurchases}
              />
            </TableData>
            <TableData>{fundingRatePercentage(insulationFundingRate[tierLevel])}</TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={2}
                displayType="text"
                fixedDecimalScale
                prefix="$"
                thousandSeparator
                value={calculatePromoFunds(fiberglasPurchases)}
              />
            </TableData>
          </TableRow>
          <TableRow>
            <TableData className="left">
              <ProductName>{loosefillFieldName}</ProductName>
            </TableData>
            <TableData>
              <Input
                allowEmptyFormatting
                allowNegative={false}
                decimalScale={0}
                onBlur={() => trackUpdate(loosefillFieldName)}
                onValueChange={event => updateLoosefill(event)}
                prefix="$"
                thousandSeparator
                value={loosefillPurchases}
              />
            </TableData>
            <TableData>{fundingRatePercentage(insulationFundingRate[tierLevel])}</TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={2}
                displayType="text"
                fixedDecimalScale
                prefix="$"
                thousandSeparator
                value={calculatePromoFunds(loosefillPurchases)}
              />
            </TableData>
          </TableRow>
          <TableRow>
            <TableData className="left">
              <ProductName>{foamularFieldName}</ProductName>
            </TableData>
            <TableData>
              <Input
                allowEmptyFormatting
                allowNegative={false}
                decimalScale={0}
                onBlur={() => trackUpdate(foamularFieldName)}
                onValueChange={event => updateFoamular(event)}
                prefix="$"
                thousandSeparator
                value={foamularPurchases}
              />
            </TableData>
            <TableData>{fundingRatePercentage(insulationFundingRate[tierLevel])}</TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={2}
                displayType="text"
                fixedDecimalScale
                prefix="$"
                thousandSeparator
                value={calculatePromoFunds(foamularPurchases)}
              />
            </TableData>
          </TableRow>
          <TableRow>
            <TableData className="left">
              <ProductName>{thermafiberFieldName}</ProductName>
            </TableData>
            <TableData>
              <Input
                allowEmptyFormatting
                allowNegative={false}
                decimalScale={0}
                onBlur={() => trackUpdate(thermafiberFieldName)}
                onValueChange={event => updateThermafiber(event)}
                prefix="$"
                thousandSeparator
                value={thermafiberPurchases}
              />
            </TableData>
            <TableData>{fundingRatePercentage(insulationFundingRate[tierLevel])}</TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={2}
                displayType="text"
                fixedDecimalScale
                prefix="$"
                thousandSeparator
                value={calculatePromoFunds(thermafiberPurchases)}
              />
            </TableData>
          </TableRow>
          <TableRow className="divider">
            <TableData className="left">
              <ProductName>{roofingFieldName}</ProductName>
            </TableData>
            <TableData>
              <Input
                allowEmptyFormatting
                allowNegative={false}
                decimalScale={0}
                onBlur={() => trackUpdate(roofingFieldName)}
                onValueChange={event => updateRoofing(event)}
                prefix="$"
                thousandSeparator
                value={roofingPurchases}
              />
            </TableData>
            <TableData>{fundingRatePercentage(roofingFundingRate[tierLevel])}</TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={2}
                displayType="text"
                fixedDecimalScale
                prefix="$"
                thousandSeparator
                value={calculatePromoFunds(roofingPurchases, roofingFundingRate[tierLevel])}
              />
            </TableData>
          </TableRow>
        </tbody>
      </Table>
      <div>
        <BrandingText>
          Owens Corning® Brands including Masonite® Doors; per unit refers to finished unit including one slab or
          multiple components/slabs/side lite/etc.
        </BrandingText>
      </div>
      <ResultContainer>
        <H6>Tier Level</H6>
        <H2>{_.startCase(tierLevel)}</H2>
      </ResultContainer>
      <ResultContainer>
        <H6>Total Potential Funding</H6>
        <H2>
          <NumberFormat
            decimalScale={2}
            displayType="text"
            fixedDecimalScale
            prefix="$"
            thousandSeparator
            value={calculateTotalFunding()}
          />
        </H2>
      </ResultContainer>
      <div style={{ marginTop: 10, fontWeight: 'bold', color: '#d40f7d' }}>
        {tierLevel !== 'diamond'
          ? `YOU ARE ${4 - categoryCount} ${
              4 - categoryCount === 1 ? 'CATEGORY' : 'CATEGORIES'
            } AWAY FROM BECOMING A DIAMOND TIER FOR ADDITIONAL FUNDING!`
          : 'THANK YOU FOR BEING A DIAMOND DEALER!'}
      </div>
    </Container>
  );
};

export default PadpPromoCalc;
